import React from "react";
import { Box, Tooltip } from "@mui/material";
import {
  CheckCircleOutline as CheckCircleIcon,
  PlayArrowRounded as PlayIcon,
  EditNoteOutlined as UpdateIcon,
  TaskAltOutlined as CompleteIcon,
  HourglassBottomRounded as WaitingIcon,
  AccessTimeOutlined as TimeIcon,
  PriorityHighRounded as UrgentIcon,
  PrecisionManufacturingOutlined as MachineIcon,
  BusinessOutlined as CompanyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Offer, OfferStatusCode } from "../models/models";
import { dt } from "../../../shared/themes/redesignTokens";
import ServiceLifecycleTracker from "../../shared/components/ServiceLifecycleTracker";
import { getProviderLifecycleStages } from "../../shared/utils/lifecycleStages";

dayjs.extend(relativeTime);

// ════════════════════════════════════════════════════════════════
//  Column theme colors
// ════════════════════════════════════════════════════════════════

export const columnThemes: Record<number, { accent: string; bg: string; glow: string; label: string }> = {
  [OfferStatusCode.awaitingConfirmation]: { accent: "#F59E0B", bg: "#FFFBEB", glow: "rgba(245,158,11,0.10)", label: "amber" },
  [OfferStatusCode.confirmed]: { accent: "#14B8A6", bg: "#F0FDFA", glow: "rgba(20,184,166,0.10)", label: "teal" },
  [OfferStatusCode.workInProgress]: { accent: "#3B82F6", bg: "#EFF6FF", glow: "rgba(59,130,246,0.10)", label: "blue" },
  [OfferStatusCode.workCompleted]: { accent: "#22C55E", bg: "#F0FDF4", glow: "rgba(34,197,94,0.10)", label: "green" },
};

// ════════════════════════════════════════════════════════════════
//  Priority helpers
// ════════════════════════════════════════════════════════════════

const priorityConfig: Record<string, { color: string; label: string }> = {
  urgent: { color: "#DC2626", label: "Urgent" },
  high: { color: "#F59E0B", label: "High" },
};

const getPriorityFromOffer = (offer: Offer): string | null => {
  // Try to extract priority from the service request issue text or other available data
  // The SR object on the offer has id, internalNumber, issue, machine
  return null; // Will be extended when we have priority data on the offer model
};

// ════════════════════════════════════════════════════════════════
//  Time-in-status helper
// ════════════════════════════════════════════════════════════════

const getTimeInStatus = (offer: Offer): string => {
  let referenceDate: string | undefined;

  switch (offer.statusCode) {
    case OfferStatusCode.awaitingConfirmation:
      // Time since offer was accepted (modifiedDate is when acceptance happened)
      referenceDate = offer.modifiedDate;
      break;
    case OfferStatusCode.confirmed:
      referenceDate = offer.confirmedByProviderDate || offer.modifiedDate;
      break;
    case OfferStatusCode.workInProgress:
      referenceDate = offer.modifiedDate;
      break;
    case OfferStatusCode.workCompleted:
      referenceDate = offer.modifiedDate;
      break;
    default:
      referenceDate = offer.modifiedDate;
  }

  if (!referenceDate) return "";
  return dayjs(referenceDate).fromNow();
};

// ════════════════════════════════════════════════════════════════
//  Props
// ════════════════════════════════════════════════════════════════

interface ActiveWorkCardProps {
  offer: Offer;
  index: number;
  translate: (key: string, options?: any) => any;
  onConfirmService: (offer: Offer) => void;
  onStartWork: (offer: Offer) => void;
  onMarkComplete: (offer: Offer) => void;
  onViewOffer: (offer: Offer) => void;
}

// ════════════════════════════════════════════════════════════════
//  ActiveWorkCard
// ════════════════════════════════════════════════════════════════

const ActiveWorkCard: React.FC<ActiveWorkCardProps> = ({
  offer,
  index,
  translate,
  onConfirmService,
  onStartWork,
  onMarkComplete,
  onViewOffer,
}) => {
  const navigate = useNavigate();
  const theme = columnThemes[offer.statusCode] || columnThemes[OfferStatusCode.workInProgress];
  const timeInStatus = getTimeInStatus(offer);

  // Truncate text helper
  const truncate = (text: string | undefined, max: number): string => {
    if (!text) return "";
    return text.length > max ? text.substring(0, max) + "..." : text;
  };

  // ── Action button per status ──

  const renderActionButton = () => {
    switch (offer.statusCode) {
      case OfferStatusCode.awaitingConfirmation:
        return (
          <Box
            onClick={(e) => { e.stopPropagation(); onConfirmService(offer); }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              backgroundColor: "#F59E0B",
              color: "#FFFFFF",
              borderRadius: "6px",
              padding: "5px 12px",
              fontSize: "11px",
              fontWeight: 700,
              fontFamily: dt.font,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.2s ease",
              animation: "offerRing 2s ease-in-out infinite",
              "&:hover": { backgroundColor: "#D97706", transform: "translateY(-1px)" },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: "14px" }} />
            {translate("activeWork.confirmService")}
          </Box>
        );
      case OfferStatusCode.confirmed:
        return (
          <Box
            onClick={(e) => { e.stopPropagation(); onStartWork(offer); }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              backgroundColor: "#14B8A6",
              color: "#FFFFFF",
              borderRadius: "6px",
              padding: "5px 12px",
              fontSize: "11px",
              fontWeight: 700,
              fontFamily: dt.font,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.2s ease",
              "&:hover": { backgroundColor: "#0D9488", transform: "translateY(-1px)" },
            }}
          >
            <PlayIcon sx={{ fontSize: "14px" }} />
            {translate("activeWork.startWork")}
          </Box>
        );
      case OfferStatusCode.workInProgress:
        return (
          <Box sx={{ display: "flex", gap: "6px" }}>
            <Tooltip title={translate("activeWork.markComplete")} arrow>
              <Box
                onClick={(e) => { e.stopPropagation(); onMarkComplete(offer); }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  backgroundColor: "#3B82F6",
                  color: "#FFFFFF",
                  borderRadius: "6px",
                  padding: "5px 10px",
                  fontSize: "11px",
                  fontWeight: 700,
                  fontFamily: dt.font,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: "#2563EB", transform: "translateY(-1px)" },
                }}
              >
                <CompleteIcon sx={{ fontSize: "14px" }} />
                {translate("activeWork.markComplete")}
              </Box>
            </Tooltip>
          </Box>
        );
      case OfferStatusCode.workCompleted:
        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              backgroundColor: "rgba(34,197,94,0.08)",
              color: "#16A34A",
              borderRadius: "6px",
              padding: "5px 12px",
              fontSize: "11px",
              fontWeight: 600,
              fontFamily: dt.font,
              whiteSpace: "nowrap",
              border: "1px solid rgba(34,197,94,0.2)",
            }}
          >
            <WaitingIcon sx={{ fontSize: "14px", animation: "spin 2s linear infinite" }} />
            {translate("activeWork.waitingForCustomer")}
          </Box>
        );
      default:
        return null;
    }
  };

  // ── Deadline / Scheduled date info ──

  const renderDateInfo = () => {
    if (offer.statusCode === OfferStatusCode.awaitingConfirmation && offer.confirmationDeadline) {
      const deadline = dayjs(offer.confirmationDeadline);
      const now = dayjs();
      const isOverdue = deadline.isBefore(now);
      const minutesLeft = deadline.diff(now, "minute");
      const isUrgent = minutesLeft <= 60 && minutesLeft > 0;

      return (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "10px",
          fontFamily: dt.fontMono,
          fontWeight: 600,
          color: isOverdue ? "#DC2626" : isUrgent ? "#F59E0B" : dt.textMuted,
          letterSpacing: "0.3px",
          ...(isUrgent && {
            animation: "pulse 1.5s ease-in-out infinite",
          }),
        }}>
          <TimeIcon sx={{ fontSize: "12px" }} />
          {isOverdue
            ? translate("activeWork.deadlinePassed")
            : `${translate("activeWork.deadline")}: ${deadline.format("HH:mm")}`
          }
        </Box>
      );
    }

    if (offer.statusCode === OfferStatusCode.confirmed && offer.confirmedArrivalDate) {
      return (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "10px",
          fontFamily: dt.fontMono,
          fontWeight: 500,
          color: "#14B8A6",
          letterSpacing: "0.3px",
        }}>
          <TimeIcon sx={{ fontSize: "12px" }} />
          {dayjs(offer.confirmedArrivalDate).format("DD MMM")}
          {offer.confirmedArrivalTimeFrom && ` ${offer.confirmedArrivalTimeFrom}`}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      onClick={() => onViewOffer(offer)}
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
        border: `1px solid ${dt.cardBorder}`,
        borderLeft: `3px solid ${theme.accent}`,
        padding: "14px",
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        overflow: "hidden",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          transform: "translateY(-2px)",
          borderLeftColor: theme.accent,
        },
      }}
    >
      {/* ── Row 1: Offer ID + time in status ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "8px" }}>
        <Box sx={{
          color: dt.textLink,
          fontWeight: 600,
          fontSize: "11px",
          fontFamily: dt.fontMono,
          letterSpacing: "0.3px",
        }}>
          {offer.internalNumber}
        </Box>
        {timeInStatus && (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: "3px",
            color: dt.textMuted,
            fontSize: "10px",
            fontFamily: dt.fontMono,
            letterSpacing: "0.2px",
          }}>
            <TimeIcon sx={{ fontSize: "11px" }} />
            {timeInStatus}
          </Box>
        )}
      </Box>

      {/* ── Row 2: SR issue (truncated) ── */}
      <Box sx={{
        color: dt.textPrimary,
        fontWeight: 500,
        fontSize: "13px",
        fontFamily: dt.font,
        lineHeight: "18px",
        mb: "8px",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}>
        {truncate(offer.serviceRequest?.issue, 80)}
      </Box>

      {/* ── Row 3: Customer + Machine ── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", mb: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <CompanyIcon sx={{ fontSize: "12px", color: dt.textMuted }} />
          <Box sx={{
            color: dt.textSecondary,
            fontSize: "11px",
            fontFamily: dt.font,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {offer.customer?.name}
          </Box>
        </Box>
        {offer.serviceRequest?.machine && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <MachineIcon sx={{ fontSize: "12px", color: dt.textMuted }} />
            <Box sx={{
              color: dt.textMuted,
              fontSize: "11px",
              fontFamily: dt.font,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {[offer.serviceRequest.machine.brandName, offer.serviceRequest.machine.name]
                .filter(Boolean)
                .join(" ")}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Row 4: Date info (deadline / arrival) ── */}
      {renderDateInfo() && (
        <Box sx={{ mb: "10px" }}>
          {renderDateInfo()}
        </Box>
      )}

      {/* ── Row 5: Lifecycle mini dots ── */}
      <Box sx={{ mb: "8px" }}>
        <ServiceLifecycleTracker stages={getProviderLifecycleStages(offer.statusCode)} variant="mini" />
      </Box>

      {/* ── Row 6: Action button ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: "8px", borderTop: `1px solid ${dt.rowBorder}` }}>
        {renderActionButton()}
        <Box sx={{
          color: dt.textLink,
          fontSize: "10px",
          fontFamily: dt.fontMono,
          fontWeight: 500,
          letterSpacing: "0.2px",
          opacity: 0.7,
          transition: "opacity 0.15s",
          "&:hover": { opacity: 1 },
        }}>
          {offer.serviceRequest?.internalNumber}
        </Box>
      </Box>
    </Box>
  );
};

export default ActiveWorkCard;
